/**
 * Serialize-once handling for the SRP view model in pageContext.data.
 *
 * buildSrpViewModel returns a model that carries copies of data the +data payload already has at
 * its top level: `initialResults` (same hits as `data.initialResults`), `initialFacetsData` (same
 * object as `data.facetsData`), plus `jobs` and `facets`, two reshaped forms of those same hits and
 * facets that only the mobile app's `/api/srp-state` consumer reads. Vike serializes
 * pageContext.data as JSON, so every copy used to ship in the HTML again (roughly half of the
 * `vike_pageContext` script on a typical SRP).
 *
 * +data.ts now puts the wire form (those four fields dropped) on `data.srpViewModel`, and every web
 * reader goes through `resolveSrpViewModel(data)`, which re-attaches `initialResults` and
 * `initialFacetsData` from the canonical top-level fields. `jobs`/`facets` come back empty: no web
 * render path reads them (SrpApp/SrpView render from `initialResults` via SearchProvider and from
 * `initialFacetsData` via FacetsProvider). `/api/srp-state` still gets the full model, it calls
 * +data.ts's `loadSrpPageData` directly.
 */
import type { SrpViewModel } from './SrpViewModel';

/** Fields of SrpViewModel that duplicate top-level pageContext.data fields (or are mobile-only). */
type SrpViewModelDuplicateKey = 'jobs' | 'facets' | 'initialResults' | 'initialFacetsData';

export type SrpWireViewModel = Omit<SrpViewModel, SrpViewModelDuplicateKey> & {
  /** Marks a model whose duplicate fields were dropped; resolveSrpViewModel restores them. */
  _wire: true;
};

export function toWireSrpViewModel(model: SrpViewModel): SrpWireViewModel {
  const { jobs: _jobs, facets: _facets, initialResults: _results, initialFacetsData: _facetsData, ...rest } = model;
  return { ...rest, _wire: true };
}

/** The top-level pageContext.data fields a wire view model is resolved against. */
export interface SrpPageDataForViewModel {
  srpViewModel?: unknown;
  initialResults?: unknown;
  facetsData?: unknown;
}

// Keyed on the wire model object so repeated renders (LayoutDefault re-renders, the island loader)
// get the same resolved object, and with it the same `initialResults` reference that SrpView's
// `results === model.initialResults` backend-unavailable check relies on.
const resolvedCache = new WeakMap<object, SrpViewModel>();

function isViewModelShaped(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v._fetch === true) return false;
  return typeof v.locale === 'string';
}

/**
 * Returns the full SrpViewModel for rendering from pageContext.data, or null when the payload has
 * no usable model (missing, or the legacy `{_fetch:true}` bootstrap stub). A model that was never
 * reduced to wire form (tests, older callers) is returned as-is.
 */
export function resolveSrpViewModel(data: SrpPageDataForViewModel | null | undefined): SrpViewModel | null {
  const model = data?.srpViewModel;
  if (!isViewModelShaped(model)) return null;
  if (model._wire !== true) {
    return Array.isArray(model.jobs) ? (model as unknown as SrpViewModel) : null;
  }
  const cached = resolvedCache.get(model);
  if (cached) return cached;
  const { _wire, ...rest } = model;
  const resolved = {
    ...rest,
    jobs: [],
    facets: {},
    initialResults: (data?.initialResults as SrpViewModel['initialResults']) ?? null,
    initialFacetsData: (data?.facetsData as SrpViewModel['initialFacetsData']) ?? undefined,
  } as unknown as SrpViewModel;
  resolvedCache.set(model, resolved);
  return resolved;
}
